import { z } from 'zod';

/**
 * Structured hiring criteria of a vacancy (MD-Vacante-Modelo-Creacion).
 *
 * The old model was free text — a `description` the company wrote and nobody
 * could match against. This turns the brief into typed criteria, each with the
 * spec's three-state importance selector:
 *
 * - `must`         → hard filter: a candidate that fails it is EXCLUDED.
 * - `preferred`    → ranking only: never excludes, moves the match score.
 * - `not_relevant` → stored but ignored.
 *
 * It replaces the two-column "Bueno/Obligatorio" design with ONE control per
 * criterion, which is what §2 of the MD mandates.
 *
 * Deliberately absent (§4 · compliance): nationality, gender, age and physical
 * appearance are NOT modelled as criteria at all. Making them unrepresentable
 * beats validating them away — there is no field to fill, so there is nothing
 * an admin has to police. Location, work status, languages and relocation are
 * the job-related stand-ins.
 */
export const IMPORTANCE = ['must', 'preferred', 'not_relevant'] as const;
export const ImportanceSchema = z.enum(IMPORTANCE);
export type Importance = z.infer<typeof ImportanceSchema>;

/** Numeric minimum (§3): candidate ≥ value. Never a range stored as text. */
export const NumericMinCriterionSchema = z.object({
	value: z.number().min(0).max(60),
	importance: ImportanceSchema,
});

/** Multi-select (§3): `any` = one match is enough; `all` = every one required. */
export const MultiSelectCriterionSchema = z.object({
	values: z.array(z.string().trim().min(1)).max(30),
	match: z.enum(['any', 'all']).default('any'),
	importance: ImportanceSchema,
});

/** Boolean criterion (pre-opening, high volume…). */
export const BooleanCriterionSchema = z.object({
	value: z.boolean(),
	importance: ImportanceSchema,
});

/**
 * One language, always an OBJECT — the spec is explicit that a language is
 * never a string, because the level and the importance belong to each one.
 */
export const LANGUAGE_PROFICIENCIES = ['basic', 'conversational', 'professional', 'fluent', 'native'] as const;
export const LanguageCriterionSchema = z.object({
	language: z.string().trim().min(1),
	proficiency: z.enum(LANGUAGE_PROFICIENCIES),
	importance: ImportanceSchema,
});

/** A system/tool the role uses (Opera, Micros, SAP…), from the M00 catalog + Other. */
export const SystemCriterionSchema = z.object({
	system: z.string().trim().min(1),
	importance: ImportanceSchema,
});

/**
 * Custom criterion (§3): typed, so free text can never silently become a hard
 * filter. `asFilter: false` keeps it informative-only even when `must` — the
 * spec's tenth test case.
 */
export const CustomCriterionSchema = z.object({
	label: z.string().trim().min(1).max(120),
	kind: z.enum(['boolean', 'select', 'number']),
	/** Expected value, in the shape `kind` announces. */
	expected: z.union([z.boolean(), z.string(), z.number()]),
	importance: ImportanceSchema,
	/** Only an explicit true lets this act as a filter at all. */
	asFilter: z.boolean().default(false),
});

/**
 * The vacancy's structured criteria, grouped as the MD's sections 2–6 group
 * them. Everything optional: an empty object is a vacancy that filters nothing
 * and ranks nothing, which is exactly what the legacy rows are.
 */
export const VacancyCriteriaSchema = z
	.object({
		// ---- §5.3 Experience -------------------------------------------------
		totalExpMin: NumericMinCriterionSchema,
		/** Years IN the target role — more predictive than total, says the MD. */
		roleExpMin: NumericMinCriterionSchema,
		uaeExpMin: NumericMinCriterionSchema,
		gccExpMin: NumericMinCriterionSchema,
		/** Venue types the candidate has worked (beach club, fine dining…). ANY. */
		environmentExp: MultiSelectCriterionSchema,
		luxuryExp: MultiSelectCriterionSchema,
		/** Brand experience: the MD wants it RANKING by default, never a wall. */
		brandExp: MultiSelectCriterionSchema,
		preopeningExp: BooleanCriterionSchema,
		highVolumeExp: BooleanCriterionSchema,

		// ---- §5.4 Languages & qualifications ----------------------------------
		languages: z.array(LanguageCriterionSchema).max(10),
		certifications: MultiSelectCriterionSchema,
		systems: z.array(SystemCriterionSchema).max(15),
		/**
		 * Matrix §4: specializations (cuisines, sections, PMS areas…), from the
		 * SPECIALIZATIONS catalog. Never a discard field — `specialization` maps
		 * to manual_review in AUTO_DISCARD (vacancy-rules.dto.ts), so a `must`
		 * that fails sends the candidate to a human, not to the bin.
		 */
		specializations: MultiSelectCriterionSchema,
		drivingLicense: z.object({
			value: z.enum(['not_required', 'any', 'uae_required']),
			importance: ImportanceSchema,
		}),

		// ---- §5.5 Availability -----------------------------------------------
		/** Job-related stand-in for the banned nationality filter: geography. */
		candidateLocation: MultiSelectCriterionSchema,
		/**
		 * Incorporación que pide la vacante, con su importancia (05/10).
		 *
		 * El valor es el mismo catálogo que declara el candidato (`Immediately`,
		 * `One Week`, `Two Weeks`, `One month notice`). Solo un `must` compara.
		 * `Negotiable` en el candidato no es un fallo: es un dato dudoso.
		 * `noticeDaysMax` sigue ausente a propósito (25/08).
		 */
		availability: z.object({
			value: z.string().trim().min(1),
			importance: ImportanceSchema,
		}),
		/**
		 * País del empleo, no la nacionalidad (05/10). Al menos un empleo en una
		 * de las regiones. Europa incluye España y Portugal. Latinoamérica es
		 * México, Centroamérica, el Caribe y Sudamérica: no Estados Unidos ni Canadá.
		 */
		experienceRegions: z.object({
			values: z.array(z.enum(['europe', 'latin_america'])).max(2),
			importance: ImportanceSchema,
		}),
		workStatusUae: MultiSelectCriterionSchema,
		/**
		 * Deliberately absent since 25/08: `noticeDaysMax`, the ceiling the
		 * company put on the candidate's notice period.
		 *
		 * The client asked for the field to disappear from the vacancy, and the
		 * schema is where that has to be said — `criteria` is a JSON column, so a
		 * key nobody declares is a key that would be stored verbatim and matched
		 * against forever. Leaving it out makes Zod STRIP it from any payload that
		 * still carries one (an old client, a vacancy re-edited from a copy), so
		 * it never reaches the column again.
		 *
		 * `noticePeriodDays` on the CANDIDATE profile is a different field and it
		 * STAYS: onboarding still asks for it and the person still declares it.
		 * What went away is the company's power to turn it into a filter.
		 */
		relocationAllowed: BooleanCriterionSchema,

		// ---- §5.9 Matching & process -------------------------------------------
		custom: z.array(CustomCriterionSchema).max(10),
		/**
		 * Kairos-only requirements (meeting of 7-ago): visible to the admin,
		 * NEVER serialized to the company or the candidate.
		 */
		internalRequirements: z.string().trim().max(2000),
		/**
		 * Grado, especialidad y alcance del puesto (05/10). Vacíos no se exigen.
		 * Rellenar uno lo vuelve imprescindible. El título compuesto del catálogo
		 * ya trae los suyos; estos tres son la forma de pedirlos en una vacante
		 * genérica (Food & Beverage Manager + Pre-opening).
		 */
		positionGrade: z.string().trim().min(1).max(80),
		positionSpecialty: z.string().trim().min(1).max(80),
		positionScope: z.string().trim().min(1).max(80),
	})
	.partial();

export type VacancyCriteriaDTO = z.infer<typeof VacancyCriteriaSchema>;

/** Seniority ladder (§5.1). Doubles as the Private Shortlist category. */
export const SENIORITY_LEVELS = ['operational', 'supervisor', 'management', 'senior_director'] as const;
export const SenioritySchema = z.enum(SENIORITY_LEVELS);
export type SeniorityLevel = (typeof SENIORITY_LEVELS)[number];

/** Salary terms (§5.6). Numbers in AED/month — never a range stored as text. */
export const SalaryTermsSchema = z
	.object({
		min: z.number().int().min(0).max(1_000_000),
		max: z.number().int().min(0).max(1_000_000),
		/** Hard exclusion only when the client marks it non-negotiable (§3). */
		flexibility: z.enum(['none', 'five_pct', 'ten_pct', 'negotiable']).default('negotiable'),
	})
	// E-P1-02: un rango invertido (min > max) descartaría a TODO el mundo en el
	// matching salarial sin que nadie entienda por qué. Se rechaza al entrar.
	.refine((salary) => salary.min <= salary.max, 'SALARY_RANGE_INVERTED');
export type SalaryTermsDTO = z.infer<typeof SalaryTermsSchema>;
